<template>
  <Teleport to="body">
    <!-- Backdrop -->
    <div class="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
         @click.self="$emit('cancel')">
      <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="$emit('cancel')" />

      <!-- Sheet / Dialog -->
      <div class="relative w-full sm:max-w-md bg-white dark:bg-slate-900
                  rounded-t-3xl sm:rounded-2xl shadow-xl
                  animate-slide-up sm:animate-scale-in">

        <!-- Handle (mobile) -->
        <div class="sm:hidden flex justify-center pt-3 pb-2">
          <div class="w-10 h-1 rounded-full bg-slate-200 dark:bg-slate-700" />
        </div>

        <!-- Icon -->
        <div class="flex justify-center pt-6 pb-4">
          <div class="w-16 h-16 rounded-2xl flex items-center justify-center"
               :class="variant === 'danger'
                 ? 'bg-red-50 dark:bg-red-950/50'
                 : 'bg-amber-50 dark:bg-amber-950/50'">
            <svg class="w-8 h-8"
                 :class="variant === 'danger'
                   ? 'text-red-500 dark:text-red-400'
                   : 'text-amber-500 dark:text-amber-400'"
                 fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round"
                    d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"/>
            </svg>
          </div>
        </div>

        <!-- Content -->
        <div class="px-6 pb-6 text-center">
          <h3 class="text-lg font-bold text-slate-800 dark:text-white mb-2">
            {{ title }}
          </h3>
          <p class="text-sm text-slate-600 dark:text-slate-400 mb-3">
            {{ message }}
          </p>

          <!-- Info notice -->
          <div class="bg-blue-50 dark:bg-blue-950/30 border border-blue-100 dark:border-blue-900/50 rounded-xl p-3 mb-5 text-left">
            <div class="flex gap-2.5">
              <svg class="w-4 h-4 text-blue-500 dark:text-blue-400 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"/>
              </svg>
              <div class="text-xs text-blue-700 dark:text-blue-300 leading-relaxed flex-1">
                <p class="font-semibold mb-1.5">{{ infoTitle || 'Good to know:' }}</p>

                <!-- List items if provided -->
                <ul v-if="infoItems && infoItems.length > 0" class="space-y-1.5">
                  <li v-for="(item, index) in infoItems" :key="index" class="flex items-start gap-2">
                    <svg class="w-3 h-3 text-blue-500 dark:text-blue-400 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/>
                    </svg>
                    <span>{{ item }}</span>
                  </li>
                </ul>

                <!-- Fallback to text message -->
                <p v-else>{{ infoMessage || 'This action won\'t affect any existing invoices, payments, or reports. Historical data will remain intact.' }}</p>

                <!-- Additional notes -->
                <div v-if="infoNotes" class="mt-3 pt-3 border-t border-blue-200 dark:border-blue-800">
                  <p class="whitespace-pre-line">{{ infoNotes }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div class="grid grid-cols-2 gap-3">
            <button @click="$emit('cancel')" type="button"
              class="py-3 px-4 rounded-xl border-2 border-slate-200 dark:border-slate-700
                     bg-white dark:bg-slate-800
                     text-slate-700 dark:text-slate-300 font-semibold text-sm
                     hover:bg-slate-50 dark:hover:bg-slate-700
                     active:scale-95 transition">
              Cancel
            </button>
            <button @click="handleConfirm" type="button" :disabled="loading"
              class="py-3 px-4 rounded-xl font-semibold text-sm text-white
                     active:scale-95 transition disabled:opacity-60 disabled:cursor-not-allowed"
              :class="variant === 'danger'
                ? 'bg-red-500 hover:bg-red-400 dark:bg-red-600 dark:hover:bg-red-500 shadow-sm shadow-red-500/30'
                : 'bg-amber-500 hover:bg-amber-400 dark:bg-amber-600 dark:hover:bg-amber-500 shadow-sm shadow-amber-500/30'">
              <span v-if="loading" class="inline-flex items-center gap-2">
                <svg class="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
                {{ loadingText || 'Processing...' }}
              </span>
              <span v-else>{{ confirmText || 'Confirm' }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref } from 'vue'

const props = defineProps({
  title: { type: String, required: true },
  message: { type: String, required: true },
  infoMessage: { type: String, default: '' },
  infoTitle: { type: String, default: '' },
  infoItems: { type: Array, default: () => [] },
  infoNotes: { type: String, default: '' },
  confirmText: { type: String, default: 'Confirm' },
  loadingText: { type: String, default: 'Processing...' },
  variant: { type: String, default: 'danger' } // 'danger' or 'warning'
})

const emit = defineEmits(['confirm', 'cancel'])
const loading = ref(false)

async function handleConfirm() {
  loading.value = true
  await emit('confirm')
}
</script>

<style scoped>
@keyframes slide-up {
  from { transform: translateY(100%); }
  to   { transform: translateY(0); }
}
@keyframes scale-in {
  from { transform: scale(0.95); opacity: 0; }
  to   { transform: scale(1); opacity: 1; }
}
.animate-slide-up {
  animation: slide-up 0.3s cubic-bezier(0.32, 0.72, 0, 1);
}
.animate-scale-in {
  animation: scale-in 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}
</style>
